/**
 * This file is a duplicate of the one in the `belong-next` package located in
 * `packages/belong-next/src/features/action-items/utils.ts`.
 *
 * Every time there is a change in that file we need to replicate it here.
 * It would be much better to have this extracted in a package like @belong/common but then we would also
 * need to copy all of the types there and, even though we do not update them that frequently now, a single update
 * could break the type checking.
 *
 * That being said, some extra tweaks may be necessary. For example, replacing the `Buffer` API with `btoa`.
 * One deliberate omission: the `isReadinessWalkthrough` flag the `belong-next` copy sets, because the walkthrough
 * copy it selects lives in a component this app does not have.
 *
 * Note that there are not many top-level pages left to migrate over to `belong-next` so hopefully this won't be here
 * much longer.
 */
import type {
  ActionItem,
  FlowStep,
  InvitationSentToDoMetadata,
  W9FlowToDoMetadata,
  Unit,
} from 'action-items/types';
import type {
  ActionTodoViewModel,
  FlowFlowModel,
  FlowModel,
  FlowStepModel,
  InspectionModel,
  PropertySummary,
  UserModel,
  WealthInfo,
} from 'api/models';
import { kebabCase } from 'es-toolkit';
import { WEALTH_ANALYSIS_STEPS } from './constants';

type GetActionItemsParams = {
  properties: PropertySummary[];
  flows: FlowFlowModel[];
  todos: ActionTodoViewModel[];
  user: UserModel;
  inspections: InspectionModel[];
  wealth: WealthInfo[];
};

/**
 * Get the list of all the action items a homeowner has considering the homes, to-dos and flows.
 *
 * @param properties List of the properties a homeowner has.
 * @param flows The list of all the flows a homeowner has.
 * @param todos The list of all the to-dos a homeowner has.
 *
 * @returns The list of all the action items a homeowner has.
 */
export function getActionItems({
  properties,
  flows,
  todos,
  user,
  inspections,
  wealth,
}: GetActionItemsParams): ActionItem[] {
  const units: Unit[] =
    properties.flatMap(
      (property) =>
        property.units!.map((unit) => ({
          ...unit,
          /**
           * Add the propertyId to the unit makes the property-level filtering easier.
           */
          propertyId: property?.basicInfo?.propertyId ?? '',
        })) ?? [],
    ) ?? [];

  const onboardingIncompleteActionItems = getOnboardingIncompleteActionItems({
    units,
    flows,
  });

  const invitationSentActionItems = getInvitationSentActionItems({
    units,
    todos,
    flows,
  });

  const wealthActionItems = getWealthActionItems({
    units,
    wealth,
  });

  const unlockInspectionActionItems = getUnlockInspectionActionItems({
    units,
    flows,
    user,
  });

  const pricingReportActionItems = getPricingReportActionItems({
    units,
    flows,
    user,
  });

  const improvementsReportActionItems = getImprovementsReportActionItems({
    units,
    flows,
    user,
  });

  const unlockListingActionItems = getUnlockListingActionItems({
    units,
    flows,
    user,
  });

  const homeownerLandlordInsuranceActionItems =
    getHomeownerLandlordInsuranceActionItems({
      units,
      flows,
    });

  const leaseRenewalFlowActionItems = getLeaseRenewalFlowActionItems({
    flows,
    units,
  });

  const homeownerPreMoveOutFlowActionItems =
    getHomeownerPreMoveOutFlowActionItems({
      flows,
      units,
    });

  const moveOutPricingFlowActionItems = getMoveOutPricingFlowActionItems({
    flows,
    units,
    inspections,
    user,
  });

  const moveOutImprovementsFlowActionItems =
    getMoveOutImprovementsFlowActionItems({
      flows,
      units,
      inspections,
      user,
    });

  const w9ActionItems = getW9ActionItems({
    todos,
    user,
  });

  const userPaymentFlowActionItems = properties[0]
    ? getUserPaymentFlowActionItems({
        flows,
        user,
        property: properties[0],
      })
    : [];

  return [
    ...onboardingIncompleteActionItems,
    ...invitationSentActionItems,
    ...wealthActionItems,
    ...unlockInspectionActionItems,
    ...pricingReportActionItems,
    ...improvementsReportActionItems,
    ...unlockListingActionItems,
    ...homeownerLandlordInsuranceActionItems,
    ...leaseRenewalFlowActionItems,
    ...homeownerPreMoveOutFlowActionItems,
    ...moveOutPricingFlowActionItems,
    ...moveOutImprovementsFlowActionItems,
    ...w9ActionItems,
    ...userPaymentFlowActionItems,
  ];
}

type GetWealthFlowActionItemPercentageParams = {
  wealthFlow: FlowModel;
};

/**
 * Get the percentage of the Wealth flow that has been completed.
 *
 * @param wealthFlow The Wealth flow.
 *
 * @returns The percentage of the Wealth flow that has been completed.
 */
function getWealthFlowActionItemPercentage({
  wealthFlow,
}: GetWealthFlowActionItemPercentageParams): number {
  const allSteps = wealthFlow.steps?.filter((step) => !step.isHidden) ?? [];

  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetWealthActionItemsParams = {
  units: Unit[];
  wealth: WealthInfo[];
};

function getWealthActionItems({
  units,
  wealth,
}: GetWealthActionItemsParams): ActionItem[] {
  const wealthActionItems: ActionItem[] = units
    .filter((unit) => {
      const wealthAnalysis = wealth?.find(
        (wealthAnalysis) =>
          wealthAnalysis.home?.unitId === unit.basicInfo?.unitId,
      );

      const isWealthFlowActive = ['Created', 'InProgress'].includes(
        wealthAnalysis?.flow?.status ?? '',
      );

      return wealthAnalysis?.summary?.isPublished && isWealthFlowActive;
    })
    .flatMap((unitWithWealthAnalysis) => {
      const wealthAnalysis = wealth?.find(
        (wealthAnalysis) =>
          wealthAnalysis.home?.unitId ===
          unitWithWealthAnalysis.basicInfo?.unitId,
      );

      // Sort steps to match the order in WEALTH_ANALYSIS_STEPS
      const knownStepNames = new Set(
        WEALTH_ANALYSIS_STEPS.map((s) => s.stepName),
      );
      const sortedSteps = [...(wealthAnalysis?.flow?.steps ?? [])]
        .filter(
          (step) => step.stepName != null && knownStepNames.has(step.stepName),
        )
        .sort((a, b) => {
          const indexA = WEALTH_ANALYSIS_STEPS.findIndex(
            (step) => step.stepName === a.stepName,
          );

          const indexB = WEALTH_ANALYSIS_STEPS.findIndex(
            (step) => step.stepName === b.stepName,
          );

          return indexA - indexB;
        });

      const currentStep = sortedSteps.find(
        (step) =>
          !['Cancelled', 'Completed'].includes(step.status) && !step.isHidden,
      );

      if (!currentStep) {
        return [];
      }

      const formattedStepName =
        currentStep?.stepName === 'HomeOwnerAssociation'
          ? 'hoa'
          : kebabCase(currentStep?.stepName ?? '');

      const href = `/accounts/portfolio/wealth-analysis/${formattedStepName}?flowId=${wealthAnalysis?.flow?.id}&stepId=${currentStep?.id}`;

      const percentage = getWealthFlowActionItemPercentage({
        wealthFlow: wealthAnalysis!.flow!,
      });

      return [
        {
          type: 'wealth',
          subType: 'unit',
          percentage,
          href,
          unitId: unitWithWealthAnalysis.basicInfo!.unitId!,
          propertyId: unitWithWealthAnalysis.propertyId,
        },
      ];
    });

  return wealthActionItems;
}

type GetMoveOutImprovementsFlowActionItemsParams = {
  flows: FlowFlowModel[];
  units: Unit[];
  inspections: InspectionModel[];
  user: UserModel;
};

/**
 * Get the list of all the Move Out Home Quality Plan flow action items.
 *
 * @param flows The list of all the flows a homeowner has.
 * @param units The list of all the units a homeowner has.
 * @param inspections The list of all the inspections a homeowner has.
 * @param user The user.
 *
 * @returns The list of all the Move Out Home Quality Plan flow action items.
 */
function getMoveOutImprovementsFlowActionItems({
  flows,
  units,
  inspections,
  user,
}: GetMoveOutImprovementsFlowActionItemsParams): ActionItem[] {
  const disabledMoveOutImprovementsFlowActionItems: ActionItem[] = units
    .filter((unit) => {
      const hasNonCompletedMoveOutInspection = inspections.some(
        (inspection) =>
          inspection.type === 'MoveOut' &&
          inspection.homeUniqueId === unit.basicInfo?.unitId &&
          ['Pending', 'Scheduled'].includes(inspection.status),
      );

      return hasNonCompletedMoveOutInspection;
    })
    .map((unit) => {
      return {
        type: 'move-out-improvements-flow',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
      };
    });

  const moveOutImprovementsFlows = flows.filter(
    (flow) =>
      flow.flowType === 'Improvements' &&
      flow.configuration?.flowSubType === 'MoveOut' &&
      flow.status !== 'Cancelled',
  );

  const pendingMoveOutImprovementsFlowActionItems: ActionItem[] = units
    .filter((unit) => {
      const hasCompletedMoveOutInspection = inspections.some(
        (inspection) =>
          inspection.type === 'MoveOut' &&
          inspection.homeUniqueId === unit.basicInfo?.unitId &&
          inspection.status === 'Completed',
      );

      const isOnMoveOutImprovementsFlow = moveOutImprovementsFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return hasCompletedMoveOutInspection && !isOnMoveOutImprovementsFlow;
    })
    .map((unit) => {
      return {
        type: 'move-out-improvements-flow',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        isPending: true,
      };
    });

  const moveOutImprovementsFlowActionItems = units
    .filter((unit) => {
      const isOnMoveOutImprovementsFlow = moveOutImprovementsFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return isOnMoveOutImprovementsFlow;
    })
    .map((unit) => {
      const moveOutImprovementsFlow = moveOutImprovementsFlows.find(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      )!;

      const percentage = getMoveOutImprovementsFlowActionItemPercentage({
        moveOutImprovementsFlow,
      });

      if (percentage === 1) {
        return null;
      }

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: [],
        flowId: moveOutImprovementsFlow.uniqueId,
        propertyId: unit.propertyId,
        unitId: unit.basicInfo!.unitId!,
        user,
      });

      return {
        type: 'move-out-improvements-flow',
        href: homeownerFlowRedirectLink,
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        percentage,
      };
    })
    .filter(Boolean) as ActionItem[];

  return [
    ...disabledMoveOutImprovementsFlowActionItems,
    ...pendingMoveOutImprovementsFlowActionItems,
    ...moveOutImprovementsFlowActionItems,
  ];
}

type GetMoveOutImprovementsFlowActionItemPercentageParams = {
  moveOutImprovementsFlow: FlowFlowModel;
};

/**
 * Get the percentage of the Move Out Home Quality Plan flow that has been completed.
 *
 * @param moveOutImprovementsFlow The Move Out Home Quality Plan flow.
 *
 * @returns The percentage of the Move Out Home Quality Plan flow that has been completed.
 */
function getMoveOutImprovementsFlowActionItemPercentage({
  moveOutImprovementsFlow,
}: GetMoveOutImprovementsFlowActionItemPercentageParams): number {
  const allSteps =
    moveOutImprovementsFlow.steps?.filter((step) => !step.isHidden) ?? [];
  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetMoveOutPricingFlowActionItemsParams = {
  flows: FlowFlowModel[];
  units: Unit[];
  inspections: InspectionModel[];
  user: UserModel;
};

function getMoveOutPricingFlowActionItems({
  flows,
  units,
  inspections,
  user,
}: GetMoveOutPricingFlowActionItemsParams): ActionItem[] {
  const disabledMoveOutPricingFlowActionItems: ActionItem[] = units
    .filter((unit) => {
      const hasNonCompletedMoveOutInspection = inspections.some(
        (inspection) =>
          inspection.type === 'MoveOut' &&
          inspection.homeUniqueId === unit.basicInfo?.unitId &&
          ['Pending', 'Scheduled'].includes(inspection.status),
      );

      return hasNonCompletedMoveOutInspection;
    })
    .map((unit) => {
      return {
        type: 'move-out-pricing-flow',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
      };
    });

  const moveOutPricingFlows = flows.filter(
    (flow) =>
      flow.flowType === 'Pricing' &&
      flow.configuration?.flowSubType === 'MoveOut' &&
      flow.status !== 'Cancelled',
  );

  const pendingMoveOutPricingFlowActionItems: ActionItem[] = units
    .filter((unit) => {
      const hasCompletedMoveOutInspection = inspections.some(
        (inspection) =>
          inspection.type === 'MoveOut' &&
          inspection.homeUniqueId === unit.basicInfo?.unitId &&
          inspection.status === 'Completed',
      );

      const isOnMoveOutPricingFlow = moveOutPricingFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return hasCompletedMoveOutInspection && !isOnMoveOutPricingFlow;
    })
    .map((unit) => {
      return {
        type: 'move-out-pricing-flow',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        isPending: true,
      };
    });

  const moveOutPricingFlowActionItems = units
    .filter((unit) => {
      const isOnMoveOutPricingFlow = moveOutPricingFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return isOnMoveOutPricingFlow;
    })
    .map((unit) => {
      const moveOutPricingFlow = moveOutPricingFlows.find(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      )!;

      const percentage = getMoveOutPricingFlowActionItemPercentage({
        moveOutPricingFlow,
      });

      if (percentage === 1) {
        return null;
      }

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: [],
        flowId: moveOutPricingFlow.uniqueId,
        propertyId: unit.propertyId,
        unitId: unit.basicInfo!.unitId!,
        user,
      });

      return {
        type: 'move-out-pricing-flow',
        href: homeownerFlowRedirectLink,
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        percentage,
      };
    })
    .filter(Boolean) as ActionItem[];

  return [
    ...disabledMoveOutPricingFlowActionItems,
    ...pendingMoveOutPricingFlowActionItems,
    ...moveOutPricingFlowActionItems,
  ];
}

type GetMoveOutPricingFlowActionItemPercentageParams = {
  moveOutPricingFlow: FlowFlowModel;
};

/**
 * Get the percentage of the Move Out Pricing flow that has been completed.
 *
 * @param moveOutPricingFlow The Move Out Pricing flow.
 *
 * @returns The percentage of the Move Out Pricing flow that has been completed.
 */
function getMoveOutPricingFlowActionItemPercentage({
  moveOutPricingFlow,
}: GetMoveOutPricingFlowActionItemPercentageParams): number {
  const allSteps =
    moveOutPricingFlow.steps?.filter((step) => !step.isHidden) ?? [];
  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetHomeownerPreMoveOutFlowActionItemsParams = {
  flows: FlowFlowModel[];
  units: Unit[];
};

/**
 * Get the list of all the Homeowner Pre Move Out flow action items.
 *
 * @param flows The list of all the flows a homeowner has.
 * @param units The list of all the units a homeowner has.
 *
 * @returns The list of all the Homeowner Pre Move Out flow action items.
 */
function getHomeownerPreMoveOutFlowActionItems({
  flows,
  units,
}: GetHomeownerPreMoveOutFlowActionItemsParams): ActionItem[] {
  const homeownerPreMoveOutFlows = flows.filter(
    (flow) =>
      flow.flowType === 'HomeownerPreMoveOut' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  const homeownerPreMoveOutFlowActionItems: ActionItem[] = units
    .filter((unit) => {
      const isOnHomeownerPreMoveOutFlow = homeownerPreMoveOutFlows.some(
        (homeownerPreMoveOutFlow) =>
          homeownerPreMoveOutFlow.configuration?.homeUniqueId ===
          unit.basicInfo?.unitId,
      );

      return isOnHomeownerPreMoveOutFlow;
    })
    .map((unit) => {
      const homeownerPreMoveOutFlow = homeownerPreMoveOutFlows.find(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      )!;

      const percentage = getHomeownerPreMoveOutFlowActionItemPercentage({
        homeownerPreMoveOutFlow,
      });

      const homeownerPreMoveOutFlowSteps = homeownerPreMoveOutFlow.steps?.find(
        (step) => !step.isHidden && step.status !== 'Completed',
      );

      const href = `/accounts/portfolio/pre-move-out?flowId=${homeownerPreMoveOutFlow.uniqueId}&stepId=${
        homeownerPreMoveOutFlowSteps?.uniqueId
      }&stepName=${kebabCase(homeownerPreMoveOutFlowSteps?.stepName ?? '')}`;

      return {
        type: 'homeowner-pre-move-out-flow',
        href,
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        percentage,
      };
    });

  return homeownerPreMoveOutFlowActionItems;
}

type GetHomeownerPreMoveOutFlowActionItemPercentageParams = {
  homeownerPreMoveOutFlow: FlowFlowModel;
};

/**
 * Get the percentage of the Homeowner Pre Move Out flow that has been completed.
 *
 * @param homeownerPreMoveOutFlow The Homeowner Pre Move Out flow.
 *
 * @returns The percentage of the Homeowner Pre Move Out flow that has been completed.
 */
function getHomeownerPreMoveOutFlowActionItemPercentage({
  homeownerPreMoveOutFlow,
}: GetHomeownerPreMoveOutFlowActionItemPercentageParams): number {
  const allSteps =
    homeownerPreMoveOutFlow.steps?.filter((step) => !step.isHidden) ?? [];
  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetLeaseRenewalFlowActionItemsParams = {
  flows: FlowFlowModel[];
  units: Unit[];
};

/**
 * Get the list of all the Lease Renewal flow action items.
 *
 * @param flows The list of all the flows a homeowner has.
 * @param units The list of all the units a homeowner has.
 *
 * @returns The list of all the Lease Renewal flow action items.
 */
function getLeaseRenewalFlowActionItems({
  flows,
  units,
}: GetLeaseRenewalFlowActionItemsParams): ActionItem[] {
  const leaseRenewalFlows = flows.filter(
    (flow) =>
      flow.flowType === 'HomeownerRenewal' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  const leaseRenewalFlowActionItems: ActionItem[] = units
    .filter((unit) => {
      const isOnLeaseRenewalFlow = leaseRenewalFlows.some(
        (leaseRenewalFlow) =>
          leaseRenewalFlow.configuration?.homeUniqueId ===
          unit.basicInfo?.unitId,
      );

      return isOnLeaseRenewalFlow;
    })
    .map((unit) => {
      const leaseRenewalFlow = leaseRenewalFlows.find(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      )!;

      const percentage = getLeaseRenewalFlowActionItemPercentage({
        leaseRenewalFlow,
      });

      const href = `/accounts/portfolio/properties/renewal-offer?flowId=${leaseRenewalFlow.uniqueId}`;

      return {
        type: 'lease-renewal-flow',
        href,
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        percentage,
      };
    });

  return leaseRenewalFlowActionItems;
}

type GetLeaseRenewalFlowActionItemPercentageParams = {
  leaseRenewalFlow: FlowFlowModel;
};

/**
 * Get the percentage of the Homeowner Renewal flow that has been completed.
 *
 * @param leaseRenewalFlow The Homeowner Renewal flow.
 *
 * @returns The percentage of the Homeowner Renewal flow that has been completed.
 */
function getLeaseRenewalFlowActionItemPercentage({
  leaseRenewalFlow,
}: GetLeaseRenewalFlowActionItemPercentageParams): number {
  const allSteps =
    leaseRenewalFlow.steps?.filter((step) => !step.isHidden) ?? [];
  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetUserPaymentFlowActionItemsParams = {
  flows: FlowFlowModel[];
  user: UserModel;
  property: PropertySummary;
};

/**
 * Get the list of all the User Payment flow action items.
 *
 * @param flows The list of all the flows a homeowner has.
 * @param user The user.
 * @param property The property.
 *
 * @returns The list of all the User Payment flow action items.
 */
function getUserPaymentFlowActionItems({
  flows,
  user,
  property,
}: GetUserPaymentFlowActionItemsParams): ActionItem[] {
  const userPaymentFlowActionItems: ActionItem[] = flows
    .filter(
      (flow) =>
        flow.flowType === 'UserPayment' &&
        !['Cancelled', 'Completed'].includes(flow.status),
    )
    .map((flow) => {
      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: [],
        flowId: flow.uniqueId,
        user,
        /**
         * The User Payment flow URL has a propertyId in it, probably because it was attached to a property.
         * Anyway, it is now at the user level and is marked as completed for all properties once we do it once.
         */
        propertyId: property?.basicInfo?.propertyId ?? '',
      });

      const percentage = getUserPaymentFlowActionItemPercentage({ flow });

      return {
        type: 'user-payment-flow',
        subType: 'user',
        href: homeownerFlowRedirectLink,
        percentage,
      };
    });

  return userPaymentFlowActionItems;
}

type GetUserPaymentFlowActionItemPercentageParams = {
  flow: FlowFlowModel;
};

/**
 * Get the percentage of the User Payment flow that has been completed.
 *
 * @param flow The User Payment flow.
 *
 * @returns The percentage of the User Payment flow that has been completed.
 */
function getUserPaymentFlowActionItemPercentage({
  flow,
}: GetUserPaymentFlowActionItemPercentageParams): number {
  const allSteps = flow.steps?.filter((step) => !step.isHidden) ?? [];
  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetW9ActionItemsParams = {
  todos: ActionTodoViewModel[];
  user: UserModel;
};

/**
 * Get the list of all the W9 action items.
 *
 * @param todos The list of all the to-dos a homeowner has.
 * @param user The user.
 *
 * @returns The list of all the W9 action items.
 */
function getW9ActionItems({
  todos,
  user,
}: GetW9ActionItemsParams): ActionItem[] {
  const w9ActionItems: ActionItem[] = todos
    .filter((todo) => todo.type === 'W9FlowOpen')
    .map((todo) => {
      const { flowId, legalEntityName } = todo.metadata as W9FlowToDoMetadata;

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: [],
        flowId,
        user,
      });

      return {
        type: 'w9',
        subType: 'user',
        href: homeownerFlowRedirectLink,
        legalEntityName,
      };
    });

  return w9ActionItems;
}

type GetHomeownerLandlordInsuranceActionItemsParams = {
  units: Unit[];
  flows: FlowFlowModel[];
};

/**
 * Get the list of all the Homeowner Landlord Insurance action items.
 *
 * @param units The list of all the units a homeowner has.
 * @param flows The list of all the flows a homeowner has.
 *
 * @returns The list of all the Homeowner Landlord Insurance action items.
 */
function getHomeownerLandlordInsuranceActionItems({
  units,
  flows,
}: GetHomeownerLandlordInsuranceActionItemsParams): ActionItem[] {
  const homeownerLandlordInsuranceFlows = flows.filter(
    (flow) =>
      flow.flowType === 'LandlordInsurance' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  const homeownerLandlordInsuranceActionItems: ActionItem[] = units
    .filter((unit) => {
      const isOnHomeownerLandlordInsuranceFlow =
        homeownerLandlordInsuranceFlows.some((flow) =>
          flow.configuration?.unitIds?.includes(unit.basicInfo?.unitId ?? ''),
        );

      return isOnHomeownerLandlordInsuranceFlow;
    })
    .map((unit) => {
      const homeownerLandlordInsuranceFlow =
        homeownerLandlordInsuranceFlows.find((flow) =>
          flow.configuration?.unitIds?.includes(unit.basicInfo?.unitId ?? ''),
        )!;

      const percentage = getHomeownerLandlordInsuranceActionItemPercentage({
        homeownerLandlordInsuranceFlow,
      });

      const href = `/accounts/portfolio/landlord-insurance?flowId=${homeownerLandlordInsuranceFlow.uniqueId}`;

      return {
        type: 'homeowner-landlord-insurance',
        href,
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        percentage,
      };
    });

  return homeownerLandlordInsuranceActionItems;
}

type GetHomeownerLandlordInsuranceActionItemPercentageParams = {
  homeownerLandlordInsuranceFlow: FlowFlowModel;
};

/**
 * Get the percentage of the Homeowner Landlord Insurance flow that has been completed.
 *
 * @param homeownerLandlordInsuranceFlow The Homeowner Landlord Insurance flow.
 *
 * @returns The percentage of the Homeowner Landlord Insurance flow that has been completed.
 */
function getHomeownerLandlordInsuranceActionItemPercentage({
  homeownerLandlordInsuranceFlow,
}: GetHomeownerLandlordInsuranceActionItemPercentageParams): number {
  const allSteps =
    homeownerLandlordInsuranceFlow.steps?.filter((step) => !step.isHidden) ??
    [];
  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetPricingReportActionItemsParams = {
  units: Unit[];
  flows: FlowFlowModel[];
  user: UserModel;
};

/**
 * Get the list of all the Pricing Plan action items.
 *
 * @param units The list of all the units a homeowner has.
 *
 * @returns The list of all the Pricing Plan action items.
 */
function getPricingReportActionItems({
  units,
  flows,
  user,
}: GetPricingReportActionItemsParams): ActionItem[] {
  /**
   * Non-adopted units who have been invited do not have the Pricing Flow opened until the Pricing Plan is published.
   * We still want to show the action items as disabled on these scenarios.
   */
  const invitedNonAdoptedUnits: ActionItem[] = units
    .filter(
      (unit) =>
        unit.basicInfo?.currentMilestone === 'AgreementSigned' &&
        !unit.isAdoptedAgreement &&
        unit.isPrimaryOwner,
    )
    .map((unit) => {
      return {
        type: 'pricing-report',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
      };
    });

  const pricingFlows = flows.filter(
    (flow) =>
      flow.flowType === 'Pricing' &&
      flow.configuration?.flowSubType === 'Onboarding' &&
      flow.status !== 'Cancelled',
  );

  /**
   * We add the flowId to the steps to be able to identify the flow the step belongs to.
   */
  const pricingFlowSteps = pricingFlows.flatMap(
    (flow) =>
      flow.steps?.map((step) => ({
        ...step,
        flowId: flow.uniqueId,
      })) ?? [],
  );

  /**
   * It's pretty common to unpublish a Pricing Plan that has errors.
   * If the Home Quality Plan was also published, the Post Inspection Proposal Sent milestone will have been reached.
   * However, after unpublishing the Pricing Plan, the milestone will not move back to Inspection Completed.
   * Therefore, we need to show the action item as pending.
   *
   * Plus, units whose Pricing Plan has not been published yet also have the corresponging action item.
   */
  const inspectionCompletedUnits: ActionItem[] = units
    .filter((unit) => {
      const isOnPricingFlow = pricingFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return (
        unit.basicInfo?.currentMilestone &&
        ['InspectionCompleted', 'PostInspectionProposalSent'].includes(
          unit.basicInfo.currentMilestone,
        ) &&
        !isOnPricingFlow
      );
    })
    .filter((unit) => unit.isPrimaryOwner)
    .map((unit) => {
      return {
        type: 'pricing-report',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        isPending: true,
      };
    });

  const pricingFlowUnits = units
    .filter((unit) => {
      const isOnPricingFlow = pricingFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return isOnPricingFlow;
    })
    .filter((unit) => unit.isPrimaryOwner)
    .map(({ propertyId, basicInfo }) => {
      const unitId = basicInfo!.unitId!;

      const pricingReportActionItemPercentage =
        getPricingReportActionItemPercentage({
          unitId,
          pricingFlows,
        });

      if (pricingReportActionItemPercentage === 1) {
        return;
      }

      const pricingFlow = pricingFlows.find(
        (flow) => flow.configuration?.homeUniqueId === unitId,
      );
      if (!pricingFlow) return;

      const pricingFlowId = pricingFlow.uniqueId;

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: pricingFlowSteps,
        flowId: pricingFlowId,
        propertyId,
        unitId,
        user,
      });

      return {
        type: 'pricing-report',
        href: homeownerFlowRedirectLink,
        unitId,
        propertyId,
        percentage: pricingReportActionItemPercentage,
      };
    })
    .filter(Boolean) as ActionItem[];

  return [
    ...invitedNonAdoptedUnits,
    ...inspectionCompletedUnits,
    ...pricingFlowUnits,
  ];
}

type GetPricingReportActionItemPercentageParams = {
  unitId: string;
  pricingFlows: FlowFlowModel[];
};

/**
 * Get the percentage of the Pricing flow that has been completed.
 *
 * @param unitId The id of the unit.
 * @param pricingFlows The list of all the Pricing flows.
 *
 * @todo Check if this can be unified after calculating all action item's percentages.
 *
 * @returns The percentage of the Pricing flow that has been completed.
 */
function getPricingReportActionItemPercentage({
  unitId,
  pricingFlows,
}: GetPricingReportActionItemPercentageParams): number {
  const allSteps = pricingFlows
    .filter((pricingFlow) => unitId === pricingFlow.configuration?.homeUniqueId)
    .flatMap((pricingFlow) => pricingFlow.steps ?? [])
    .filter((pricingFlowStep) => !pricingFlowStep.isHidden);

  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetImprovementsReportActionItemsParams = {
  units: Unit[];
  flows: FlowFlowModel[];
  user: UserModel;
};

/**
 * Get the list of all the Home Quality Plan action items.
 *
 * @param units The list of all the units a homeowner has.
 * @param flows The list of all the flows a homeowner has.
 * @param user The user.
 *
 * @returns The list of all the Home Quality Plan action items.
 */
function getImprovementsReportActionItems({
  units,
  flows,
  user,
}: GetImprovementsReportActionItemsParams): ActionItem[] {
  /**
   * Non-adopted units who have been invited do not have the Home Quality Plan Flow opened until the Home Quality Plan is published.
   * We still want to show the action items as disabled on these scenarios.
   */
  const invitedNonAdoptedUnits: ActionItem[] = units
    .filter(
      (unit) =>
        unit.basicInfo?.currentMilestone === 'AgreementSigned' &&
        !unit.isAdoptedAgreement &&
        unit.isPrimaryOwner,
    )
    .map((unit) => {
      return {
        type: 'improvements-report',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
      };
    });

  const improvementsFlows = flows.filter(
    (flow) =>
      flow.flowType === 'Improvements' &&
      ['Onboarding', 'QA'].includes(flow.configuration?.flowSubType ?? '') &&
      flow.status !== 'Cancelled',
  );

  /**
   * We add the flowId to the steps to be able to identify the flow the step belongs to.
   */
  const improvementsFlowSteps = improvementsFlows.flatMap(
    (flow) =>
      flow.steps?.map((step) => ({
        ...step,
        flowId: flow.uniqueId,
      })) ?? [],
  );

  const inspectionCompletedUnits: ActionItem[] = units
    .filter((unit) => {
      const isOnImprovementsFlow = improvementsFlows.some(
        (flow) => flow.configuration?.homeUniqueId === unit.basicInfo?.unitId,
      );

      return (
        unit.basicInfo?.currentMilestone &&
        ['InspectionCompleted', 'PostInspectionProposalSent'].includes(
          unit.basicInfo.currentMilestone,
        ) &&
        !isOnImprovementsFlow
      );
    })
    .filter((unit) => unit.isPrimaryOwner)
    .map((unit) => {
      return {
        type: 'improvements-report',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        isPending: true,
      };
    });

  const postInspectionFlowUnits = units
    .filter((unit) => {
      const isUnitOnImprovementsFlow = improvementsFlowSteps.some(
        (step) => step.dataUniqueId === unit.basicInfo?.unitId,
      );

      return isUnitOnImprovementsFlow;
    })
    .filter((unit) => unit.isPrimaryOwner)
    .map(({ propertyId, basicInfo }) => {
      const unitId = basicInfo!.unitId!;

      const unitFlows = improvementsFlows.filter(
        (flow) => flow.configuration?.homeUniqueId === unitId,
      );

      // A unit can carry an onboarding and a walkthrough flow at once. The
      // walkthrough is the later of the two, so the card must describe it.
      const unitFlow =
        unitFlows.find((flow) => flow.configuration?.flowSubType === 'QA') ??
        unitFlows[0];

      const improvementsReportActionItemPercentage =
        getImprovementsReportActionItemPercentage({
          unitId,
          improvementsFlows: unitFlow ? [unitFlow] : [],
        });

      if (improvementsReportActionItemPercentage === 1) {
        return;
      }

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: improvementsFlowSteps,
        flowId: unitFlow?.uniqueId,
        propertyId,
        unitId,
        user,
      });

      return {
        type: 'improvements-report',
        href: homeownerFlowRedirectLink,
        unitId,
        propertyId,
        percentage: improvementsReportActionItemPercentage,
      };
    })
    .filter(Boolean) as ActionItem[];

  return [
    ...invitedNonAdoptedUnits,
    ...inspectionCompletedUnits,
    ...postInspectionFlowUnits,
  ];
}

type GetImprovementsReportActionItemPercentageParams = {
  unitId: string;
  improvementsFlows: FlowFlowModel[];
};

/**
 * Get the percentage of the Home Qulity Plan flow that has been completed.
 *
 * @param unitId The id of the unit.
 * @param improvementsFlows The list of all the Home Quality Plan flows.
 *
 * @todo Check if this can be unified after calculating all action item's percentages.
 *
 * @returns The percentage of the Home Quality Plan flow that has been completed.
 */
function getImprovementsReportActionItemPercentage({
  unitId,
  improvementsFlows,
}: GetImprovementsReportActionItemPercentageParams): number {
  const allSteps = improvementsFlows
    .filter(
      (improvementsFlow) =>
        unitId === improvementsFlow.configuration?.homeUniqueId,
    )
    .flatMap((improvementsFlow) => improvementsFlow.steps ?? [])
    .filter((improvementFlowStep) => !improvementFlowStep.isHidden);

  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetUnlockListingActionItemsParams = {
  units: Unit[];
  flows: FlowFlowModel[];
  user: UserModel;
};

/**
 * Get the list of all the unlock listing action items.
 *
 * @param units The list of all the units a homeowner has.
 * @param flows The list of all the flows a homeowner has.
 * @param user The user.
 *
 * @todo Check if this can be unified after calculating all of the setup flow action items.
 *
 * @returns The list of all the unlock listing action items.
 */
function getUnlockListingActionItems({
  units,
  flows,
  user,
}: GetUnlockListingActionItemsParams): ActionItem[] {
  const inspectionFlows = flows.filter(
    (flow) =>
      flow.flowType === 'HomeInspection' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  /**
   * We add the flowId to the steps to be able to identify the flow the step belongs to.
   */
  const inspectionFlowSteps = inspectionFlows.flatMap(
    (flow) =>
      flow.steps?.map((step) => ({
        ...step,
        flowId: flow.uniqueId,
      })) ?? [],
  );

  /**
   * We show the action item as disabled since the Home Listing flow depends on some information coming from the Inspection flow.
   */
  const disabledListingActionItems: ActionItem[] = units
    .filter((unit) => {
      const isUnitOnInspectionFlow = inspectionFlowSteps.some(
        (step) => step.dataUniqueId === unit.basicInfo?.unitId,
      );

      return (
        isUnitOnInspectionFlow &&
        unit.basicInfo?.currentMilestone === 'AgreementSigned'
      );
    })
    .map((unit) => {
      return {
        type: 'unlock-listing',
        href: '',
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        isPending: true,
      };
    });

  const unlockListingFlows = flows.filter(
    (flow) =>
      flow.flowType === 'HomeListing' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  /**
   * We add the flowId to the steps to be able to identify the flow the step belongs to.
   */
  const unlockListingFlowSteps = unlockListingFlows.flatMap(
    (flow) =>
      flow.steps?.map((step) => ({
        ...step,
        flowId: flow.uniqueId,
      })) ?? [],
  );

  const unlockListingActionItems = units
    .filter((unit) => {
      const isUnitOnListingFlow = unlockListingFlowSteps.some(
        (step) => step.dataUniqueId === unit.basicInfo?.unitId,
      );

      return isUnitOnListingFlow;
    })
    .map(({ propertyId, basicInfo, isAdoptedAgreement }) => {
      const unitId = basicInfo!.unitId!;

      const listingFlowId = unlockListingFlowSteps.find(
        (step) => step.dataUniqueId === unitId,
      )?.flowId;
      const listingFlow = flows.find(
        (flow) => flow.uniqueId === listingFlowId,
      )!;

      const unlockListingActionItemPercentage =
        getUnlockListingActionItemPercentage({
          listingFlow,
        });

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: [],
        flowId: listingFlowId,
        propertyId,
        unitId,
        user,
      });

      /**
       * The user profile step is a part of the listing flow but shown as an standalone page.
       * Since that step has its dataUniqueId set to the userId, the percentage will be 1 after
       * successfully completing the rest of the steps that are indeed attached to the property or the unit.
       */
      if (unlockListingActionItemPercentage === 1) {
        return;
      }

      return {
        type: 'unlock-listing',
        href: homeownerFlowRedirectLink,
        unitId,
        propertyId,
        percentage: unlockListingActionItemPercentage,
        isAdopted: isAdoptedAgreement,
      };
    })
    .filter(Boolean) as ActionItem[];

  return [...disabledListingActionItems, ...unlockListingActionItems];
}

type GetUnlockListingActionItemPercentageParams = {
  listingFlow: FlowFlowModel;
};

/**
 * Get the percentage of the home listing flow that has been completed.
 * To calculate this we are considering the corresponding property's steps.
 * For example, if the property has 3 units, the total number of steps for each unit is the sum of all of the steps
 * which have the `unitId` as the step's `dataUniqueId` plus all of the steps whose `dataUniqueId` is `propertyId`.
 *
 * @param listingFlow The Home Listing flow that the unit belongs to.
 *
 * @todo Check if this can be unified after calculating all action item's percentages.
 *
 * @returns The percentage of the home listing flow that has been completed.
 */
function getUnlockListingActionItemPercentage({
  listingFlow,
}: GetUnlockListingActionItemPercentageParams): number {
  const allSteps = listingFlow.steps?.filter((step) => !step.isHidden) ?? [];

  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetUnlockInspectionActionItemsParams = {
  units: Unit[];
  flows: FlowFlowModel[];
  user: UserModel;
};

/**
 * Get the list of all the unlock Walkthrough action items.
 *
 * @param units The list of all the units a homeowner has.
 * @param flows The list of all the flows a homeowner has.
 * @param user The user.
 *
 * @returns The list of all the unlock Walkthrough action items.
 */
function getUnlockInspectionActionItems({
  units,
  flows,
  user,
}: GetUnlockInspectionActionItemsParams): ActionItem[] {
  const unlockInspectionFlows = flows.filter(
    (flow) =>
      flow.flowType === 'HomeInspection' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  /**
   * We add the flowId to the steps to be able to identify the flow the step belongs to.
   */
  const unlockInspectionFlowSteps = unlockInspectionFlows.flatMap(
    (flow) =>
      flow.steps?.map((step) => ({
        ...step,
        flowId: flow.uniqueId,
      })) ?? [],
  );

  const unlockInspectionActionItems: ActionItem[] = units
    .filter((unit) => {
      const isUnitOnInspectionFlow = unlockInspectionFlowSteps.some(
        (step) => step.dataUniqueId === unit.basicInfo?.unitId,
      );

      return isUnitOnInspectionFlow;
    })
    .map(({ propertyId, basicInfo, isAdoptedAgreement }) => {
      const unitId = basicInfo!.unitId!;

      const unlockInspectionActionItemPercentage =
        getUnlockInspectionActionItemPercentage({
          unitId,
          unlockInspectionFlowSteps,
          propertyId,
        });

      const homeownerFlowRedirectLink = getHomeownerFlowRedirectLink({
        flowSteps: unlockInspectionFlowSteps,
        propertyId,
        unitId,
        user,
      });

      return {
        type: 'unlock-inspection',
        href: homeownerFlowRedirectLink,
        unitId,
        propertyId,
        percentage: unlockInspectionActionItemPercentage,
        isAdopted: isAdoptedAgreement,
      };
    });

  return unlockInspectionActionItems;
}

type GetHomeownerFlowRedirectLinkParams = {
  flowSteps: FlowStep[];
  flowId?: string;
  propertyId?: string;
  unitId?: string;
  user: UserModel;
};

/**
 * Get the link to the homeowner flow internal router.
 * This route will figure out the next step to redirect to based on the flow and the user's data.
 *
 * @param flowSteps The list of all the steps of the flow.
 * @param propertyId The id of the property.
 * @param unitId The id of the unit.
 * @param user The user.
 *
 * @returns The link to the homeowner flow internal router.
 */
function getHomeownerFlowRedirectLink({
  flowSteps,
  flowId,
  propertyId,
  unitId,
  user,
}: GetHomeownerFlowRedirectLinkParams): string {
  let currentFlowId = flowId;

  if (!currentFlowId) {
    const step = flowSteps.find(({ dataUniqueId }) => {
      return dataUniqueId && [unitId, propertyId].includes(dataUniqueId);
    });
    currentFlowId = step?.flowId;
  }

  const { firstName, lastName, email, phone, userId } = user;

  const encodedUserData = btoa(
    JSON.stringify({ firstName, lastName, email, phone, userId }),
  );

  const urlSearchParams = new URLSearchParams({
    flowId: currentFlowId ?? '',
    data: encodedUserData,
  });

  if (propertyId) {
    urlSearchParams.set('homeId', propertyId);
  }

  if (unitId) {
    urlSearchParams.set('unitId', unitId);
  }

  return `/redirect/flows/homeowner?${urlSearchParams.toString()}`;
}

type GetUnlockInspectionActionItemPercentageParams = {
  unitId: string;
  unlockInspectionFlowSteps: FlowStepModel[];
  propertyId: string;
};

/**
 * Get the percentage of the Home Quality Plan flow that has been completed.
 * To calculate this we are considering the corresponding property's steps.
 * For example, if the property has 3 units, the total number of steps for each unit is the sum of all of the steps
 * which have the `unitId` as the step's `dataUniqueId` plus all of the steps whose `dataUniqueId` is `propertyId`.
 *
 * @param unitId The id of the unit.
 * @param unlockInspectionFlowSteps The list of all the steps of the Home Quality Plan flow.
 * @param propertyId The id of the property.
 *
 * @todo Check if this can be unified after calculating all action item's percentages.
 *
 * @returns The percentage of the Home Quality Plan flow that has been completed.
 */
function getUnlockInspectionActionItemPercentage({
  unitId,
  unlockInspectionFlowSteps,
  propertyId,
}: GetUnlockInspectionActionItemPercentageParams): number {
  const allSteps = unlockInspectionFlowSteps.filter(
    (step) =>
      [unitId, propertyId].includes(step.dataUniqueId!) && !step.isHidden,
  );

  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetInvitationSentActionItemsParams = {
  units: Unit[];
  todos: ActionTodoViewModel[];
  flows: FlowFlowModel[];
};

/**
 * Get the list of all the invitation sent action items.
 *
 * @param units The list of all the units a homeowner has.
 * @param todos The list of all the to-dos a homeowner has.
 * @param flows The list of all the flows a homeowner has.
 *
 * @returns The list of all the invitation sent action items with the corresponding link to the last uncompleted step of the flow.
 */
function getInvitationSentActionItems({
  units,
  todos,
  flows,
}: GetInvitationSentActionItemsParams): ActionItem[] {
  const invitationFlows = flows.filter(
    (flow) =>
      flow.flowType === 'Agreement' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  const invitationSentToDos = todos.filter(
    (todo) => todo.type === 'AgreementFlowOpen',
  );

  const invitationSentActionItems: ActionItem[] = invitationSentToDos
    .flatMap(({ metadata }) => {
      const { homesInAgreement, flowId, flowStepId } =
        metadata as InvitationSentToDoMetadata;

      return homesInAgreement.map(({ homeUniqueId }) => {
        return {
          unitId: homeUniqueId,
          flowId,
          flowStepId,
        };
      });
    })
    .flatMap(({ unitId, flowId }) => {
      const currentUnit = units.find(
        (unit) => unit.basicInfo?.unitId === unitId,
      );

      if (!currentUnit) {
        return [];
      }

      const { propertyId, isAdoptedAgreement } = currentUnit;

      const invitationFlow = invitationFlows.find(
        (flow) => flow.uniqueId === flowId,
      )!;

      const percentage = getInvitationSentActionItemPercentage({
        invitationFlow,
      });

      return [
        {
          type: 'invitation-sent',
          href: '',
          unitId,
          propertyId,
          percentage,
          isAdopted: isAdoptedAgreement,
        },
      ];
    });

  return invitationSentActionItems;
}

type GetInvitationSentActionItemPercentageParams = {
  invitationFlow: FlowFlowModel;
};

/**
 * Get the percentage of the invitation flow that has been completed.
 *
 * @param invitationFlow The invitation flow.
 *
 * @returns The percentage of the invitation flow that has been completed.
 */
function getInvitationSentActionItemPercentage({
  invitationFlow,
}: GetInvitationSentActionItemPercentageParams): number {
  const allSteps =
    invitationFlow?.steps?.filter((step) => !step.isHidden) ?? [];
  const completedSteps = allSteps?.filter(
    (step) => step.status === 'Completed',
  );

  return completedSteps.length / allSteps.length;
}

type GetOnboardingIncompleteActionItemsParams = {
  units: Unit[];
  flows: FlowFlowModel[];
};

/**
 * Get the list of all the incompleted onboardings formatted as action items. They consist of:
 * - The type of the action item being 'onboarding-incomplete'.
 * - The href to the Onboarding main flow page.
 * - The unit id.
 * - The percentage of the flow that has been completed.
 *
 * @param units List of the units a homeowner has.
 * @param flows The list of all the flows.
 *
 * @returns The list of all the incompleted onboardings formatted as action items.
 */
function getOnboardingIncompleteActionItems({
  units,
  flows,
}: GetOnboardingIncompleteActionItemsParams): ActionItem[] {
  const onboardingFlows = flows.filter(
    (flow) =>
      flow.flowType === 'HomeownerSignUp' &&
      !['Cancelled', 'Completed'].includes(flow.status),
  );

  const onboardingIncompleteActionItems = units
    .filter((unit) => !unit.basicInfo?.isOnboardingComplete)
    .map((unit) => {
      const onboardingFlowSteps = onboardingFlows.flatMap(
        (flow) => flow.steps ?? [],
      );

      const percentage = getOnboardingIncompleteActionItemPercentage({
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        onboardingFlowSteps,
      });

      /**
       * When the onboarding flow has multiple units, a unit can have all of its steps completed yet the flow
       * is opened for the rest of the units. We don't want to show the action item in this case.
       */
      if (percentage === 1) {
        return;
      }

      return {
        type: 'onboarding-incomplete',
        href: `/homeowners/sign-up/${unit.basicInfo?.unitId}`,
        unitId: unit.basicInfo!.unitId!,
        propertyId: unit.propertyId,
        percentage,
      };
    })
    .filter(Boolean) as ActionItem[];

  return onboardingIncompleteActionItems;
}

type GetOnboardingIncompleteActionItemPercentageParams = {
  unitId: string;
  onboardingFlowSteps: FlowStepModel[];
  propertyId: string;
};

/**
 * Get the percentage of the onboarding flow that has been completed.
 * To calculate this we are considering the corresponding property's steps.
 * For example, if the property has 3 units, the total number of steps for each unit is the sum of all of the steps
 * which have the `unitId` as the step's `dataUniqueId` plus all of the steps whose `dataUniqueId` is `propertyId`.
 *
 * @param unitId The id of the unit.
 * @param onboardingFlowSteps The list of all the steps of the onboarding flow.
 * @param propertyId The id of the property.
 *
 * @returns The percentage of the onboarding flow that has been completed.
 */
function getOnboardingIncompleteActionItemPercentage({
  unitId,
  onboardingFlowSteps,
  propertyId,
}: GetOnboardingIncompleteActionItemPercentageParams): number {
  const allSteps = onboardingFlowSteps.filter(
    (step) =>
      [unitId, propertyId].includes(step.dataUniqueId!) && !step.isHidden,
  );

  const completedSteps = allSteps.filter((step) => step.status === 'Completed');

  return completedSteps.length / allSteps.length;
}

type GetPropertyActionItemsParams = {
  property: PropertySummary;
  actionItems: ActionItem[];
};

/**
 * Get the list of all the action items a property has.
 *
 * @param property The active property.
 * @param actionItems The list of all the action items a homeowner has.
 *
 * @returns The list of all the action items a property has.
 */
export function getPropertyActionItems({
  property,
  actionItems,
}: GetPropertyActionItemsParams): ActionItem[] {
  const propertyActionItems = actionItems.filter(
    (actionItem) =>
      actionItem.subType !== 'user' &&
      actionItem.propertyId === property.basicInfo?.propertyId,
  );

  return propertyActionItems;
}
